
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export interface PendingApproval {
  id: string;
  title: string;
  content: string | null;
  creator_id: string;
  approval_requested_at: string;
  approval_notes: string | null;
  privacy_level: string;
  creator_profile?: {
    first_name: string;
    last_name: string;
    username: string;
  };
}

export const useTimelineApprovals = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch pending approvals for timeline owner
  const { data: pendingApprovals, isLoading } = useQuery({
    queryKey: ['pending-approvals', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('profile_timeline_entries')
        .select(`
          id,
          title,
          content,
          creator_id,
          approval_requested_at,
          approval_notes,
          privacy_level,
          profiles!creator_id (
            first_name,
            last_name,
            username
          )
        `)
        .eq('user_id', user.id)
        .eq('requires_approval', true)
        .eq('is_approved', false)
        .not('creator_id', 'eq', user.id)
        .order('approval_requested_at', { ascending: false });

      if (error) {
        console.error('Error fetching pending approvals:', error);
        throw error;
      }

      return data?.map(entry => ({
        ...entry,
        creator_profile: entry.profiles
      })) || [];
    },
    enabled: !!user?.id
  });

  // Approve timeline entry
  const approveEntry = useMutation({
    mutationFn: async ({ entryId, notes }: { entryId: string; notes?: string }) => {
      const { error } = await supabase
        .from('profile_timeline_entries')
        .update({
          is_approved: true,
          approval_notes: notes || null
        })
        .eq('id', entryId)
        .eq('user_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['timeline-entries'] });
      toast.success('Entry approved successfully');
    },
    onError: (error) => {
      console.error('Error approving entry:', error);
      toast.error('Failed to approve entry');
    }
  });

  // Reject timeline entry
  const rejectEntry = useMutation({
    mutationFn: async ({ entryId, notes }: { entryId: string; notes?: string }) => {
      const { error } = await supabase
        .from('profile_timeline_entries')
        .delete()
        .eq('id', entryId)
        .eq('user_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
      toast.success('Entry rejected and removed');
    },
    onError: (error) => {
      console.error('Error rejecting entry:', error);
      toast.error('Failed to reject entry');
    }
  });

  return {
    pendingApprovals: pendingApprovals || [],
    isLoading,
    approveEntry: approveEntry.mutate,
    rejectEntry: rejectEntry.mutate,
    isApproving: approveEntry.isPending,
    isRejecting: rejectEntry.isPending
  };
};
